import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { userManagementService, PlatformUser, ListUsersParams } from '../services/userManagement';
import { Table, TableColumn } from '../components/Table';
import './UsersPage.css';

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'customer' | 'provider'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const itemsPerPage = 50; // Increased from 20 to show more users per page

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadUsers reads from state
  }, [currentPage, userTypeFilter, searchTerm]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListUsersParams = {
        limit: itemsPerPage,
        search: searchTerm || undefined,
        userType: userTypeFilter !== 'all' ? userTypeFilter : undefined,
      };

      if (currentPage > 1 && lastEvaluatedKey) {
        params.lastEvaluatedKey = lastEvaluatedKey;
      }

      const response = await userManagementService.listUsers(params);
      setUsers(response.users);
      setTotalCount(response.count);
      setLastEvaluatedKey(
        response.lastEvaluatedKey
          ? encodeURIComponent(JSON.stringify(response.lastEvaluatedKey))
          : undefined
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
    loadUsers();
  };

  const handleUserTypeFilter = (type: 'all' | 'customer' | 'provider') => {
    setUserTypeFilter(type);
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleViewUser = (userId: string) => {
    navigate(`/dashboard/users/${userId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns: TableColumn<PlatformUser>[] = useMemo(() => [
    {
      header: 'Name',
      accessor: 'name',
    },
    {
      header: 'Email',
      accessor: 'email',
    },
    {
      header: 'Phone',
      accessor: 'phone',
      hideOnTablet: true,
    },
    {
      header: 'Type',
      accessor: 'userType',
      render: (value) => (
        <span className={`user-type-badge ${value as string}`}>
          {value as string}
        </span>
      ),
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: (value) => formatDate(value as string),
      hideOnMobile: true,
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <button
          className="view-button"
          onClick={() => handleViewUser(row.userId)}
        >
          View
        </button>
      ),
      dataLabel: '',
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleViewUser is stable
  ], []);

  return (
    <div className="users-page">
      <div className="users-header">
        <h1>User Management</h1>
        <div className="users-stats">
          <div className="stat-card">
            <div className="stat-value">{totalCount}</div>
            <div className="stat-label">Total Users</div>
          </div>
        </div>
      </div>

      <div className="users-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">
            Search
          </button>
        </form>

        <div className="filter-buttons">
          <button
            className={`filter-button ${userTypeFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleUserTypeFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-button ${userTypeFilter === 'customer' ? 'active' : ''}`}
            onClick={() => handleUserTypeFilter('customer')}
          >
            Customers
          </button>
          <button
            className={`filter-button ${userTypeFilter === 'provider' ? 'active' : ''}`}
            onClick={() => handleUserTypeFilter('provider')}
          >
            Providers
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <>
          <Table
            columns={columns}
            data={users}
            getRowId={(row) => row.userId}
            emptyMessage="No users found"
            containerClassName="users-table-container"
            className="users-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || users.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

